import type { InfiniteData } from '@tanstack/react-query';
import {
  formatAmountInput,
  formatEuro,
  parseAmountInput,
  unitAvailability,
  unitAvailabilityLabels,
} from '@brimo/shared';
import { isManagedField } from '../availability';
import { formatNumber, parseDecimal } from '../format';
import type { PatchUnitBody, UnitDetail, UnitRow, UnitsResponse } from '../types';

/**
 * Inline editing of the units table of Objekte 03 (Nico Kremer's feedback 01.11.2026, D-365): „Miete Ist",
 * „Miete Soll", „MEA" as click-to-edit fields and „Freigabe" as select-on-click, each saved on its own through the
 * existing `PATCH /units/:id`, optimistic in the table, reverted with the server message on error.
 */

export type InlineField = 'rentActualCents' | 'rentTargetCents' | 'coOwnershipShare' | 'salesReleased';

/** Field labels for the accessible names of the inline editors. */
export const INLINE_LABELS: Record<InlineField, string> = {
  rentActualCents: 'Miete Ist',
  rentTargetCents: 'Miete Soll',
  coOwnershipShare: 'MEA',
  salesReleased: 'Freigabe',
};

export const RELEASE_OPTIONS = [
  { value: 'yes', label: 'Freigegeben' },
  { value: 'no', label: 'Nicht freigegeben' },
] as const;

/** Editable inline: the property may be edited and the field is not owned by TeamProQ (import-only). */
export function canEditInline(row: Pick<UnitRow, 'external'>, field: InlineField, canEdit: boolean): boolean {
  return canEdit && !isManagedField(row.external, field);
}

/** The text a numeric editor starts with („1.180,00", „41,2", empty). */
export function inlineText(field: Exclude<InlineField, 'salesReleased'>, row: UnitRow): string {
  const value = row[field];
  if (value === null) return '';
  return field === 'coOwnershipShare' ? formatNumber(value, 4) : formatAmountInput(value);
}

/** Display value of a numeric cell („1.180 €", „41,2", „—"). */
export function inlineDisplay(field: Exclude<InlineField, 'salesReleased'>, row: UnitRow): string {
  const value = row[field];
  if (value === null) return '—';
  return field === 'coOwnershipShare' ? formatNumber(value, 4) : formatEuro(value);
}

export type ParsedInline = { ok: true; value: number | null } | { ok: false; message: string };

/** The editor text → the stored value (cents / share); empty clears the value; negative or unreadable is rejected. */
export function parseInline(field: Exclude<InlineField, 'salesReleased'>, text: string): ParsedInline {
  if (text.trim() === '') return { ok: true, value: null };
  if (field === 'coOwnershipShare') {
    const value = parseDecimal(text);
    if (value === null || value < 0 || value > 10_000)
      return { ok: false, message: 'Bitte eine Zahl angeben.' };
    return { ok: true, value };
  }
  const cents = parseAmountInput(text);
  if (cents === null || cents < 0) return { ok: false, message: 'Bitte einen Betrag ab 0 € angeben.' };
  return { ok: true, value: cents };
}

/** The PATCH body of one inline change. */
export function inlineBody(field: InlineField, value: number | boolean | null): PatchUnitBody {
  if (field === 'salesReleased') return { salesReleased: value === true };
  return { [field]: value as number | null };
}

/** The row with one field changed; the availability follows a release change (`unitAvailability`). */
export function patchedRow(row: UnitRow, field: InlineField, value: number | boolean | null): UnitRow {
  if (field === 'salesReleased') {
    const salesReleased = value === true;
    const availability = unitAvailability({ ...row, salesReleased });
    return {
      ...row,
      salesReleased,
      availability,
      availabilityLabel: unitAvailabilityLabels[availability],
    };
  }
  return { ...row, [field]: value as number | null };
}

/** The row with the server's answer of the PATCH. */
export function rowFromDetail(row: UnitRow, detail: UnitDetail): UnitRow {
  return {
    ...row,
    salesReleased: detail.fields.salesReleased,
    availability: detail.availability,
    availabilityLabel: detail.availabilityLabel,
    rentActualCents: detail.fields.rentActualCents,
    rentTargetCents: detail.fields.rentTargetCents,
    coOwnershipShare: detail.fields.coOwnershipShare,
    actions: detail.actions,
  };
}

/** Replaces one row in every loaded page of the table. */
export function replaceRow(
  data: InfiniteData<UnitsResponse> | undefined,
  unitId: string,
  update: (row: UnitRow) => UnitRow,
): InfiniteData<UnitsResponse> | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.map((row) => (row.id === unitId ? update(row) : row)),
    })),
  };
}

export interface InlineSaveSteps {
  /** Applies the optimistic row and returns the snapshot to restore. */
  apply: () => InfiniteData<UnitsResponse> | undefined;
  save: () => Promise<UnitDetail>;
  commit: (detail: UnitDetail) => void;
  revert: (snapshot: InfiniteData<UnitsResponse> | undefined) => void;
  fail: (error: unknown) => void;
}

/** Optimistic save of one inline change: apply → PATCH → the server row, or revert + error toast. */
export async function runInlineSave(steps: InlineSaveSteps): Promise<boolean> {
  const snapshot = steps.apply();
  try {
    steps.commit(await steps.save());
    return true;
  } catch (error) {
    steps.revert(snapshot);
    steps.fail(error);
    return false;
  }
}
